import { useState } from "react";
import { ActionIcon, Group, Menu, rem, Tooltip } from "@mantine/core";
import {
  createJob,
  createTrainingSet,
  deleteImages,
} from "../api";
import {
  IconCoins,
  IconDeselect,
  IconEraser,
  IconStack,
  IconTrash,
  IconUserPlus,
} from "@tabler/icons-react";
import mixpanel from "../tracking";
import { notifications } from "@mantine/notifications";
import { useStore } from "../store";
import { useLocation } from "wouter";
import { imageThumbUri } from "./imageUris";

const isDev = import.meta.env.VITE_DEV === "true";

const operations = [
  { id: "removebg", label: "Remove Background", credits: 30 },
  { id: "upscale", label: "Upscale", credits: 30 },
  ...(isDev ? [{ id: "test", label: "Test", credits: 1 }] : []),
];

/**
 * Utilities for the current gallery selection, rendered in the bottom bar.
 * Generation (image/video) lives in the generator tabs; everything here is a
 * per-image operation or selection management.
 */
export default function SelectionActions({ onDeselect, onCreateBundle }) {
  const selectedImages = useStore((state) => state.selectedImages);
  const setSelectedImages = useStore((state) => state.setSelectedImages);
  const jobs = useStore((state) => state.jobs);
  const setJobs = useStore((state) => state.setJobs);
  const galleryImages = useStore((state) => state.galleryImages);
  const setGalleryImages = useStore((state) => state.setGalleryImages);
  const [, setLocation] = useLocation();
  const [busyOp, setBusyOp] = useState(null);

  function getPixelImage(logicalImageId) {
    return imageThumbUri(galleryImages, logicalImageId);
  }

  const applyOperation = async (operation) => {
    try {
      mixpanel.track("applyOperation batch called", {
        batchSize: selectedImages.length,
        operationId: operation.id,
      });
    } catch {}

    setBusyOp(operation.id);
    for (const image of selectedImages) {
      try {
        const data = await createJob({
          operation: operation.id,
          inputImageId: image,
          highQuality: false,
        });
        const newJob = {
          uri: getPixelImage(image),
          clientId: Math.random().toString(36).substr(2, 9),
          ...data,
        };
        setJobs([...jobs, newJob]);
      } catch (error) {
        notifications.show({
          title: "Error creating job",
          message: `Failed to create job for image: ${error.message}`,
          color: "red",
        });
      }
    }
    setBusyOp(null);
  };

  const iconStyle = { width: rem(14), height: rem(14) };
  const totalCredits =
    operations[0].credits * Math.max(selectedImages.length, 1);

  return (
    <Group gap="xs">
      <Menu position="top" withinPortal>
        <Menu.Target>
          <ActionIcon variant="default" title="Edit images" loading={!!busyOp}>
            <IconEraser style={iconStyle} stroke={1.5} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>{selectedImages.length} image(s) selected</Menu.Label>
          {operations.map((operation) => (
            <Menu.Item
              key={operation.id}
              disabled={selectedImages.length === 0 || !!busyOp}
              onClick={() => applyOperation(operation)}
              rightSection={
                <Group gap={2}>
                  <IconCoins
                    stroke={1.5}
                    style={{ width: rem(12), height: rem(12) }}
                  />
                  {operation.credits * selectedImages.length}
                </Group>
              }
            >
              {operation.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      <Tooltip label="Delete selected" position="top">
        <ActionIcon
          variant="default"
          c="red"
          loading={busyOp === "delete"}
          onClick={() => {
            setBusyOp("delete");
            deleteImages(selectedImages)
              .then(() => {
                setGalleryImages((prev) => {
                  if (!prev || !prev.data) return prev;
                  const filteredData = prev.data.filter(
                    (img) => !selectedImages.includes(img.logicalImageId)
                  );
                  return {
                    ...prev,
                    data: filteredData,
                    totalCount: filteredData.length,
                  };
                });
                setSelectedImages([]);
                notifications.show({
                  title: "Success",
                  message: "Selected images deleted",
                  color: "green",
                });
              })
              .catch((error) => {
                notifications.show({
                  title: "Error",
                  message: error.message,
                  color: "red",
                });
              })
              .finally(() => setBusyOp(null));
          }}
          disabled={selectedImages.length === 0}
        >
          <IconTrash style={iconStyle} stroke={1.5} />
        </ActionIcon>
      </Tooltip>

      <ActionIcon
        variant="default"
        title="Deselect all"
        onClick={onDeselect}
        disabled={selectedImages.length === 0}
      >
        <IconDeselect style={{ width: rem(14), height: rem(14) }} />
      </ActionIcon>
      <ActionIcon
        variant="default"
        title="Group into bundle"
        onClick={onCreateBundle}
        disabled={selectedImages.length === 0}
      >
        <IconStack style={{ width: rem(14), height: rem(14) }} />
      </ActionIcon>
      <Tooltip label={`Create model (${totalCredits} cr min)`}>
        <ActionIcon
          variant="default"
          title="Create model"
          disabled={selectedImages.length === 0}
          onClick={() => {
            createTrainingSet(selectedImages)
              .then((data) => {
                notifications.show({
                  title: "Success",
                  message: "Training set created",
                  color: "green",
                });
                setLocation(`/training-datasets/${data.id}`);
              })
              .catch((error) => {
                notifications.show({
                  title: "An error occurred",
                  message: "" + error,
                  color: "red",
                });
              });
          }}
        >
          <IconUserPlus style={{ width: rem(14), height: rem(14) }} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}
